/**
 * Nested live card for a delegated Work subagent — title, model, and the
 * tool it is on right now. Mirrors the compact hierarchy Cursor uses so the
 * transcript shows what the helper is doing instead of a one-line "started".
 */
import React from 'react';
import { View, StyleSheet } from 'react-native';
import PropTypes from 'prop-types';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import {
  workSubagentActivityText,
  workSubagentModelLabel,
} from '@/constants/work';

function SubagentGlyph({ color }) {
  return (
    <View style={styles.glyph} accessibilityElementsHidden>
      <View style={[styles.dot, { backgroundColor: color }]} />
      <View style={[styles.dot, { backgroundColor: color }]} />
    </View>
  );
}

SubagentGlyph.propTypes = {
  color: PropTypes.string.isRequired,
};

function WorkSubagentCard({ line, isLive = false }) {
  const { colors } = useTheme();
  const title = String(line?.text || line?.label || 'Researcher').trim();
  const meta = workSubagentModelLabel(line);
  const activity = workSubagentActivityText(line);
  const running = isLive && (line?.status === 'started' || line?.status === 'progress');
  const label = [title, meta, activity].filter(Boolean).join('. ');

  return (
    <View
      style={styles.card}
      accessibilityRole="text"
      accessibilityLabel={running ? `${label}. Waiting for subagent` : label}
    >
      <SubagentGlyph color={colors.text_color_tertiary} />
      <View style={styles.body}>
        <View style={styles.headline}>
          <Text
            size="n"
            weight="semibold"
            color={running ? 'primary' : 'secondary'}
            numberOfLines={1}
            style={styles.title}
          >
            {title}
          </Text>
          {!!meta && (
            <Text size="s" color="tertiary" numberOfLines={1} style={styles.meta}>
              {meta}
            </Text>
          )}
        </View>
        {!!activity && (
          <Text size="n" color="tertiary" numberOfLines={2} style={styles.activity}>
            {activity}
          </Text>
        )}
      </View>
    </View>
  );
}

WorkSubagentCard.propTypes = {
  line: PropTypes.object.isRequired,
  isLive: PropTypes.bool,
};

const styles = StyleSheet.create({
  card: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
  },
  glyph: {
    width: 18,
    marginTop: 6,
    alignItems: 'center',
    gap: 3,
  },
  dot: {
    width: 3,
    height: 3,
    borderRadius: 1.5,
  },
  body: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  headline: {
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: 8,
  },
  title: {
    flexShrink: 1,
  },
  meta: {
    flexShrink: 0,
  },
  activity: {
    flex: 1,
  },
});

function sameCard(prev, next) {
  return prev.isLive === next.isLive
    && prev.line?.taskId === next.line?.taskId
    && prev.line?.text === next.line?.text
    && prev.line?.status === next.line?.status
    && prev.line?.model === next.line?.model
    && prev.line?.lastTool === next.line?.lastTool
    && prev.line?.okCount === next.line?.okCount
    && prev.line?.partialCount === next.line?.partialCount
    && prev.line?.role === next.line?.role
    && prev.line?.lastToolArgs?.path === next.line?.lastToolArgs?.path
    && prev.line?.lastToolArgs?.query === next.line?.lastToolArgs?.query;
}

export default React.memo(WorkSubagentCard, sameCard);
